import React, { useState } from 'react';
import { TIMER_PRESETS, FOCUS_TAGS } from '../../utils/timeUtils';
import { useSubscription } from '../../hooks/useSubscription';
import { useAppStore } from '../../store/useAppStore';
import UpgradePrompt from '../Subscription/UpgradePrompt';

const ALL_PLANT_VARIETIES = [
  { id: 'roma', label: 'Roma', premium: false },
  { id: 'cherry', label: 'Cherry', premium: false },
  { id: 'beefsteak', label: 'Beefsteak', premium: true },
  { id: 'heirloom', label: 'Heirloom', premium: true },
  { id: 'sunGold', label: 'Sun Gold', premium: true },
  { id: 'sanMarzano', label: 'San Marzano', premium: true },
  { id: 'greenZebra', label: 'Green Zebra', premium: true },
  { id: 'blackKrim', label: 'Black Krim', premium: true },
];

const PLANT_COLORS = {
  roma: { fruit: '#ef4444', leaf: '#4ade80' },
  cherry: { fruit: '#f87171', leaf: '#86efac' },
  beefsteak: { fruit: '#dc2626', leaf: '#22c55e' },
  heirloom: { fruit: '#f97316', leaf: '#4ade80' },
  sunGold: { fruit: '#fbbf24', leaf: '#86efac' },
  sanMarzano: { fruit: '#b91c1c', leaf: '#22c55e' },
  greenZebra: { fruit: '#84cc16', leaf: '#4ade80' },
  blackKrim: { fruit: '#7f1d1d', leaf: '#86efac' },
};

function PlantIcon({ species }) {
  const colors = PLANT_COLORS[species] || PLANT_COLORS.roma;
  return (
    <svg viewBox="0 0 48 48" width="32" height="32" aria-hidden="true">
      <line x1="24" y1="40" x2="24" y2="20" stroke="#2d5016" strokeWidth="2.5" strokeLinecap="round" />
      <ellipse cx="18" cy="24" rx="5" ry="3" fill={colors.leaf} transform="rotate(-20 18 24)" />
      <ellipse cx="30" cy="28" rx="5" ry="3" fill={colors.leaf} transform="rotate(20 30 28)" opacity="0.85" />
      <circle cx="22" cy="30" r="4" fill={colors.fruit} />
      <circle cx="28" cy="22" r="3.5" fill={colors.fruit} opacity="0.9" />
    </svg>
  );
}

function LockIcon({ className = '' }) {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" className={className} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
      <rect x="3" y="7" width="10" height="7" rx="1.5" />
      <path d="M5 7V5a3 3 0 016 0v2" />
    </svg>
  );
}

export default function TimerSetup({ onStart, sessionsToday = 0, dailyLimit = Infinity, sessionsRemaining, isPremium = false }) {
  const [selectedPreset, setSelectedPreset] = useState('pomodoro');
  const [customMinutes, setCustomMinutes] = useState(30);
  const [showCustom, setShowCustom] = useState(false);
  const [selectedTag, setSelectedTag] = useState('work');
  const [selectedPlant, setSelectedPlant] = useState('roma');
  const [showPlantUpgrade, setShowPlantUpgrade] = useState(false);
  const { isPlantUnlocked } = useSubscription();
  const strictMode = useAppStore((s) => s.strictMode);
  const graceSeconds = strictMode ? 15 : 30;

  const handlePlantSelect = (plant) => {
    if (!isPlantUnlocked(plant.id)) {
      setShowPlantUpgrade(true);
      return;
    }
    setSelectedPlant(plant.id);
  };

  const handleStart = () => {
    const minutes = showCustom
      ? customMinutes
      : TIMER_PRESETS.find((p) => p.id === selectedPreset)?.minutes || 25;
    onStart(minutes, selectedTag, selectedPlant);
  };

  return (
    <div className="max-w-md mx-auto px-4 py-3 pb-24 space-y-4 flex flex-col">
      <header className="text-center py-1 space-y-1">
        <h2 className="text-2xl font-bold tracking-tight bg-gradient-to-br from-[color:var(--color-text-primary)] to-[color:var(--color-accent-light)] bg-clip-text text-transparent">
          Ready to focus?
        </h2>
        <p className="text-sm text-[color:var(--color-text-muted)]">
          {sessionsToday === 0
            ? 'Start your first session today'
            : `${sessionsToday} session${sessionsToday !== 1 ? 's' : ''} completed today`}
        </p>
        {!isPremium && Number.isFinite(dailyLimit) && (
          <span className="inline-block mt-1 px-2.5 py-0.5 text-xs font-semibold rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-500 dark:text-amber-400">
            {sessionsRemaining > 0
              ? `${sessionsRemaining}/${dailyLimit} free session${dailyLimit !== 1 ? 's' : ''} remaining`
              : 'Daily limit reached'}
          </span>
        )}
      </header>

      <section className="space-y-2">
        <label className="block text-xs font-semibold uppercase tracking-widest text-[color:var(--color-text-muted)] pl-0.5">
          Duration
        </label>
        <div className="grid grid-cols-2 gap-2.5">
          {TIMER_PRESETS.map((preset) => {
            const active = selectedPreset === preset.id && !showCustom;
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => { setSelectedPreset(preset.id); setShowCustom(false); }}
                aria-pressed={active}
                className={[
                  'relative flex flex-col items-center justify-center gap-1 min-h-[64px] px-1 py-2 rounded-xl backdrop-blur-glass transition-all duration-300 cursor-pointer overflow-hidden',
                  active
                    ? 'bg-tomato-500/10 border border-tomato-500/40 shadow-glow-sm'
                    : 'bg-[color:var(--glass-bg)] border border-[color:var(--glass-border)] hover:-translate-y-0.5 hover:shadow-card hover:border-[color:var(--glass-border-hover)]',
                ].join(' ')}
              >
                <span className="text-xl leading-none drop-shadow">{preset.icon}</span>
                <span className="text-sm font-semibold text-[color:var(--color-text-primary)]">{preset.label}</span>
                <span className="text-xs font-medium text-[color:var(--color-text-muted)]">{preset.minutes}m</span>
                {active && <span className="absolute bottom-0 left-1/5 right-1/5 h-0.5 bg-gradient-to-r from-transparent via-tomato-500 to-transparent rounded-full" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => setShowCustom(!showCustom)}
          className={`mx-auto block px-4 py-1 text-xs font-medium transition-opacity ${showCustom ? 'text-[color:var(--color-text-muted)]' : 'text-[color:var(--color-accent)] opacity-70 hover:opacity-100'}`}
        >
          {showCustom ? 'Use preset' : 'Custom duration'}
        </button>
        {showCustom && (
          <div className="flex flex-col items-center gap-3 p-5 rounded-xl bg-[color:var(--glass-bg)] border border-[color:var(--glass-border)] backdrop-blur-glass">
            <input
              type="range"
              min="5"
              max="120"
              step="5"
              value={customMinutes}
              onChange={(e) => setCustomMinutes(Number(e.target.value))}
              className="w-full h-1 cursor-pointer accent-tomato-500"
              aria-label="Custom duration in minutes"
            />
            <span className="text-xl font-bold tracking-tight text-[color:var(--color-accent-light)]">{customMinutes} minutes</span>
          </div>
        )}
      </section>

      <section className="space-y-2">
        <label className="block text-xs font-semibold uppercase tracking-widest text-[color:var(--color-text-muted)] pl-0.5">
          What are you focusing on?
        </label>
        <div className="flex flex-wrap gap-2">
          {FOCUS_TAGS.map((tag) => {
            const active = selectedTag === tag.id;
            return (
              <button
                key={tag.id}
                type="button"
                onClick={() => setSelectedTag(tag.id)}
                aria-pressed={active}
                style={active ? { borderColor: tag.color, background: `color-mix(in srgb, ${tag.color} 12%, transparent)`, boxShadow: `0 0 16px color-mix(in srgb, ${tag.color} 20%, transparent)` } : undefined}
                className={[
                  'px-3.5 py-1.5 rounded-full text-xs font-medium transition-all duration-300 backdrop-blur-glass',
                  active
                    ? 'text-[color:var(--color-text-primary)] border'
                    : 'border border-white/5 bg-white/[0.04] text-[color:var(--color-text-secondary)] hover:bg-white/[0.07] hover:border-white/10 hover:-translate-y-0.5',
                ].join(' ')}
              >
                {tag.label}
              </button>
            );
          })}
        </div>
      </section>

      <section className="space-y-2">
        <label className="block text-xs font-semibold uppercase tracking-widest text-[color:var(--color-text-muted)] pl-0.5">
          Plant variety
        </label>
        <div className="grid grid-cols-4 gap-2">
          {ALL_PLANT_VARIETIES.map((plant) => {
            const unlocked = isPlantUnlocked(plant.id);
            const active = selectedPlant === plant.id;
            return (
              <button
                key={plant.id}
                type="button"
                onClick={() => handlePlantSelect(plant)}
                aria-pressed={active}
                aria-label={unlocked ? plant.label : `${plant.label} (Premium)`}
                className={[
                  'relative flex flex-col items-center gap-0.5 px-1 py-2 rounded-xl backdrop-blur-glass cursor-pointer transition-all duration-300 text-xs font-medium',
                  active
                    ? 'bg-tomato-500/10 border border-tomato-500/40 text-[color:var(--color-text-primary)] shadow-glow-sm'
                    : 'bg-[color:var(--glass-bg)] border border-[color:var(--glass-border)] text-[color:var(--color-text-secondary)] hover:-translate-y-0.5 hover:border-[color:var(--glass-border-hover)]',
                  !unlocked ? 'opacity-60' : '',
                ].join(' ')}
              >
                <PlantIcon species={plant.id} />
                <span>{plant.label}</span>
                {!unlocked && <LockIcon className="absolute top-1.5 right-1.5 text-amber-500 dark:text-amber-400" />}
                {!unlocked && <span className="text-[9px] font-semibold uppercase tracking-wide text-amber-500 dark:text-amber-400">Premium</span>}
              </button>
            );
          })}
        </div>
      </section>

      <p className="text-[11px] text-center text-[color:var(--color-text-muted)] flex items-center justify-center gap-1.5">
        <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <path d="M8 1.5l-6 3v4c0 3.5 2.5 5.5 6 6 3.5-0.5 6-2.5 6-6v-4l-6-3z" strokeLinejoin="round" />
        </svg>
        Leave the app and your plant wilts after {graceSeconds}s
        {strictMode && <span className="font-semibold text-tomato-500"> · Strict</span>}
      </p>

      <button
        type="button"
        onClick={handleStart}
        aria-label="Start focus session"
        className="relative w-full min-h-[48px] px-8 py-3.5 rounded-xl text-lg font-bold tracking-tight text-[color:var(--color-bg-primary)] dark:text-soil-300 bg-gradient-to-br from-tomato-700 via-tomato-500 to-tomato-500 shadow-[0_4px_20px_rgba(239,68,68,0.3),0_0_40px_rgba(239,68,68,0.1),inset_0_1px_0_rgba(255,255,255,0.15)] hover:-translate-y-0.5 hover:shadow-[0_6px_28px_rgba(239,68,68,0.4),0_0_60px_rgba(239,68,68,0.15),inset_0_1px_0_rgba(255,255,255,0.2)] active:translate-y-0 active:scale-[0.98] transition-all duration-300 overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/15 before:to-transparent before:pointer-events-none"
      >
        Grow & Focus
      </button>

      {showPlantUpgrade && (
        <UpgradePrompt
          reason="Unlock all plant varieties with Premium. Grow beefsteak, heirloom, sun gold and more!"
          onDismiss={() => setShowPlantUpgrade(false)}
        />
      )}
    </div>
  );
}
